import { useState } from "react";
import {
  Card,
  CardContent,
  Button,
  Typography,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  Box,
  InputAdornment,
  IconButton,
  Divider,
} from "@mui/material";
import {
  Person as PersonIcon,
  Lock as LockIcon,
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import AdminApi from "../../components/AdminApi/Api/api";
import { setLoginDetails } from "../../store/AdminStore/action";
import { useSelector, useDispatch } from "react-redux";

export default function AdminLogin() {
  const [adminValues, setAdminValues] = useState({
    user_name: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [isLogginIn, setIsLoggingIn] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const globalState = useSelector((state) => state.userReducer);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  const showDialog = (type, message) => {
    setSnackbar({
      open: true,
      message: message,
      severity: type,
    });
  };

  const closeDialog = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAdminValues({
      ...adminValues,
      [name]: value,
    });
  };

  const forgotPassword = () => navigate("/forgot-password");

  const onClickLogin = () => {
    if (!adminValues.user_name) {
      showDialog("error", "Please enter user name");
      return;
    }

    if (!adminValues.password) {
      showDialog("error", "Please enter password");
      return;
    }

    const payload = {
      user_name: adminValues.user_name,
      password: adminValues.password,
    };

    setIsLoggingIn(true);
    new AdminApi()
      .login(payload)
      .then((res) => {
        setIsLoggingIn(false);
        showDialog("success", "Admin login successful");

        dispatch(
          setLoginDetails({
            adminDetails: res?.data?.adminDetails,
            clientDetails: res?.data?.clientData,
            isAdminLoggedIn: true,
            userTokens: {
              accessToken: res?.data?.token,
            },
          })
        );

        if (res?.data?.adminDetails?.admin_role == "CLIENT") {
          navigate("/client-dashboard");
        } else {
          navigate("/Home");
        }
      })
      .catch((err) => {
        console.log(err);
        setIsLoggingIn(false);
        showDialog("error", err?.response?.data?.message || "Login failed");
      });
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)",
        p: 2,
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 450,
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            background: "linear-gradient(135deg, #00897b 0%, #00695c 100%)",
            color: "white",
            p: 3,
            textAlign: "center",
          }}
        >
          <LockIcon sx={{ fontSize: 40, mb: 1 }} />
          <Typography variant="h5" component="h1" fontWeight="bold">
            Admin Login
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
            Enter your credentials to access the admin panel
          </Typography>
        </Box>

        <CardContent sx={{ p: 3 }}>
          <TextField
            fullWidth
            label="Username"
            name="user_name"
            variant="outlined"
            value={adminValues.user_name}
            onChange={handleChange}
            disabled={isLogginIn}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Password"
            name="password"
            type={showPassword ? "text" : "password"}
            variant="outlined"
            value={adminValues.password}
            onChange={handleChange}
            disabled={isLogginIn}
            sx={{ mb: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ textAlign: "right", mb: 2 }}>
            <Button
              onClick={forgotPassword}
              sx={{
                color: "#00897b",
                textTransform: "none",
                fontSize: "0.875rem",
                fontWeight: "bold",
                "&:hover": {
                  backgroundColor: "transparent",
                  color: "#00695c",
                },
              }}
            >
              Forgot Password?
            </Button>
          </Box>

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={onClickLogin}
            disabled={
              isLogginIn || !adminValues.user_name || !adminValues.password
            }
            sx={{
              py: 1.5,
              borderRadius: 2,
              background: "linear-gradient(135deg, #00897b 0%, #00695c 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #00796b 0%, #004d40 100%)",
              },
            }}
          >
            {isLogginIn ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Login"
            )}
          </Button>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Secure Admin Portal
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeDialog}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={closeDialog}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
